import { Target, Eye, Plus } from "lucide-react";
import { PageHero, Reveal, SectionHead } from "../components/Reveal";
import { CTABanner } from "../components/CTABanner";
import { Stats } from "../components/HomeSections";
import { IMAGES } from "../data/content";

const CHAPTERS = [
  { num: "01", title: "Strategy", desc: "Every pixel and every rupee starts with a business objective. We diagnose before we prescribe." },
  { num: "02", title: "Creativity", desc: "Design and content that make people stop, feel and remember — never decoration for its own sake." },
  { num: "03", title: "Technology", desc: "Fast websites, reliable systems and ERPs that quietly power growth in the background." },
  { num: "04", title: "Marketing", desc: "Distribution is half the product. We put your brand in front of the right audience, repeatedly." },
  { num: "05", title: "Data", desc: "We measure what matters, report it honestly and let numbers steer the next move." },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHero
        eyebrow="About DG Hubs"
        title="WE BUILD DIGITAL. WE CREATE GROWTH."
        sub="DG Hubs is a digital growth and technology agency focused on helping businesses build strong brands, powerful digital experiences and scalable digital systems."
      />

      <section className="bg-dg-navy pb-24 sm:pb-32">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl shadow-2xl shadow-black/40">
              <img src={IMAGES.team} alt="DG Hubs team collaborating on a digital growth strategy" className="aspect-[4/3] w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-dg-navy/80 via-transparent to-transparent" />
              <p className="absolute bottom-5 left-5 font-display text-lg font-bold text-white">One team. Every digital discipline.</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <SectionHead
              eyebrow="Our Approach"
              title="Strategy + Creativity + Technology + Marketing + Data"
              sub="Most agencies sell you one piece of the puzzle. We built DG Hubs so a growing business never has to stitch together five vendors, five contracts and five opinions again."
            />
            <div className="-mt-6 flex flex-wrap items-center gap-2.5">
              {["Strategy", "Creativity", "Technology", "Marketing", "Data"].map((w, i) => (
                <span key={w} className="flex items-center gap-2.5">
                  <span className="rounded-full border border-dg-cyan/30 bg-dg-cyan/5 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-dg-cyan">{w}</span>
                  {i < 4 && <Plus size={13} className="text-slate-500" />}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-dg-light bg-grid-light py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead light eyebrow="The Manifesto" title="Five Chapters. One Obsession: Your Growth." />
          <div className="space-y-0">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.06}>
                <div
                  data-testid={`chapter-${c.num}`}
                  className="group grid items-center gap-4 border-t border-dg-blue/10 py-9 transition-all duration-500 last:border-b hover:bg-white sm:grid-cols-[100px_240px_1fr] sm:gap-8 sm:px-6"
                >
                  <span className="font-mono text-sm text-dg-blue/50 transition-colors group-hover:text-dg-cyan">{c.num}</span>
                  <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-dg-navy">{c.title}</h3>
                  <p className="max-w-xl text-sm sm:text-base text-slate-600">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-dg-navy2 py-24 sm:py-32 noise">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 sm:px-8 md:grid-cols-2">
          <Reveal>
            <div data-testid="mission-card" className="h-full rounded-3xl border border-white/8 bg-white/[0.03] p-9 sm:p-11">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-dg-blue to-dg-cyan text-white"><Target size={21} /></span>
              <h3 className="font-display mt-6 text-2xl font-extrabold text-white">Our Mission</h3>
              <p className="mt-3 text-base leading-relaxed text-slate-400">To make professional digital solutions accessible to businesses of every size.</p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div data-testid="vision-card" className="h-full rounded-3xl border border-white/8 bg-white/[0.03] p-9 sm:p-11">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-dg-blue to-dg-cyan text-white"><Eye size={21} /></span>
              <h3 className="font-display mt-6 text-2xl font-extrabold text-white">Our Vision</h3>
              <p className="mt-3 text-base leading-relaxed text-slate-400">To become a trusted digital growth partner for businesses globally.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <Stats />
      <CTABanner />
    </main>
  );
}
